'use client'
import { addConcession, addMerchandise } from '@/app/order/checkout/action'
import { useErrorToast, useSuccessToast } from '@/components/toastHook'
import ScrollObserver from '@a24/modules/ScrollObserver'
import type { PageDefault } from '@a24/sanity/types'
import { stegaClean } from '@sanity/client/stega'
import dynamic from 'next/dynamic'
import { useState, useTransition } from 'react'
interface IPageContent extends PageDefault {}

const SanityModule = ({
  _module,
  dataSanity,
  index
}: {
  _module?: NonNullable<IPageContent['modules']>[number]
  dataSanity: string
  index: number
}) => {
  const [actionState, setActionState] =
    useState<
      Awaited<ReturnType<typeof addConcession | typeof addMerchandise>>
    >()
  const [isPending, startTransition] = useTransition()
  const handleAddConcessionToCart = ({
    concessionId
  }: { concessionId: string }) => {
    startTransition(async () => {
      if (!concessionId) return
      const res = await addConcession({
        concessionId: concessionId
      })
      startTransition(() => setActionState(res))
    })
  }
  const handleAddMerchandiseToCart = ({
    productId,
    variantId
  }: { productId: string; variantId: string }) => {
    startTransition(async () => {
      if (!productId) return
      const res = await addMerchandise({
        merchandiseId: productId,
        priceValueId: variantId
      })
      startTransition(() => setActionState(res))
    })
  }

  useErrorToast(actionState)
  useSuccessToast(actionState, {
    overideMessage: 'Added to cart successfully!'
  })

  if (!_module) return null

  let renderModule = null
  switch (_module._type) {
    case 'ModuleCalendar':
      renderModule = (
        <DynamicModuleCalendar
          _id={_module._key}
          className='theme-light module-wrapper spacing-bottom-default spacing-top-default'
          content={{
            title: _module.title || undefined
          }}
          labels={undefined}
          sequence={index}
        />
      )
      break
    case 'ModuleDivider':
      renderModule = (
        <DynamicModuleDivider
          _id={_module._key}
          className={`theme-light module-wrapper ${stegaClean(_module.spacing?.top)} ${stegaClean(_module.spacing?.bottom)}`}
          content={{
            variant: _module.variant || undefined
          }}
          labels={undefined}
          sequence={index}
        />
      )
      break

    case 'ModuleFiftyFifty':
      renderModule = (
        <DynamicModuleFiftyFifty
          _id={_module._key}
          className={`theme-light module-wrapper ${stegaClean(_module.spacing?.top)} ${stegaClean(_module.spacing?.bottom)}`}
          content={{
            // @ts-ignore: complains of passing undefined as value
            description: _module.description,
            // @ts-ignore: update when image solution available
            image: _module.image,
            linkCta: _module.linkCta,
            linkCta2: _module.linkCta2,
            title: _module.title,
            variant: _module.variant || undefined
          }}
          labels={undefined}
          sequence={index}
        />
      )
      break
    case 'ModuleFreetext':
      renderModule = (
        <DynamicModuleFreetext
          _id={_module._key}
          className={`theme-light module-wrapper ${stegaClean(_module.spacing?.top)} ${stegaClean(_module.spacing?.bottom)}`}
          content={{
            heading: _module.heading || undefined,
            // @ts-ignore: complains of passing undefined as value
            text: _module.copy
          }}
          labels={undefined}
          sequence={index}
        />
      )
      break
    case 'ModuleGallery':
      renderModule = (
        <DynamicModuleGallery
          _id={_module._key}
          className={`theme-light module-wrapper ${stegaClean(_module.spacing?.top)} ${stegaClean(_module.spacing?.bottom)}`}
          content={{
            // @ts-ignore: complains of passing undefined as value
            description: _module.description,
            // @ts-ignore: update when image solution available
            images: _module.images,
            title: _module.title
          }}
          labels={undefined}
          sequence={index}
        />
      )
      break
    case 'ModuleGrid':
      renderModule = (
        <DynamicModuleGrid
          _id={_module._key}
          className='theme-light module-wrapper spacing-bottom-default spacing-top-default'
          content={{
            cardType: _module.cardType || undefined,
            title: _module.title || undefined,
            handleAddConcessionToCart,
            handleAddMerchandiseToCart,
            isPending: isPending
          }}
          labels={undefined}
          sequence={index}
        />
      )
      break
    case 'ModuleHero':
      renderModule = (
        <DynamicModuleHero
          _id={_module._key}
          className='theme-dark-transparent module-wrapper'
          content={{
            // @ts-ignore: update when solution available
            cards: _module.cardsToDisplay,
            // @ts-ignore: update when image solution available
            image: _module.image,
            title: _module.title || undefined,
            video: {
              // @ts-ignore: update when file solution available
              highRes: _module.videoHighRes,
              // @ts-ignore: update when file solution available
              lowRes: _module.videoLowRes
            }
          }}
          labels={undefined}
          sequence={index}
        />
      )
      break
    case 'ModuleListOf':
      renderModule = (
        <DynamicModuleListOf
          _id={_module._key}
          className={`theme-light module-wrapper ${stegaClean(_module.spacing?.top)} ${stegaClean(_module.spacing?.bottom)}`}
          content={{
            title: _module.title || undefined,
            first: _module.first,
            second: _module.second,
            third: _module.third,
            fourth: _module.fourth
          }}
          labels={undefined}
          sequence={index}
        />
      )
      break
    case 'ModuleProductDetail':
      renderModule = (
        <DynamicModuleProductDetail
          _id={_module._key}
          className='theme-light module-wrapper spacing-bottom-default spacing-top-default'
          content={{
            // @ts-ignore: update when solution available
            availableToPurchase: _module.targetProduct?.availableToPurchaseFrom,
            // @ts-ignore: update when solution available
            name: _module.targetProduct?.name || 'Unknown',
            // @ts-ignore: update when solution available
            description: _module.targetProduct?.description,
            // @ts-ignore: update when solution available
            fullDescription: _module.targetProduct?.fullDescription,
            // @ts-ignore: update when solution available
            image: _module.targetProduct?.featuredImageLandscape,
            // @ts-ignore: update when solution available
            price: _module.targetProduct?.price,
            // @ts-ignore: update when solution available
            productId: _module.targetProduct?.externalId,
            // @ts-ignore: update when solution available
            relatedShow: _module.targetProduct?.relatedShow,
            // @ts-ignore: update when solution available
            variants: _module.targetProduct?.variants,
            // @ts-ignore: update when solution available
            merchId: _module.targetProduct?.merchExternalId,
            handleAddMerchandiseToCart,
            isPending: isPending
          }}
          labels={undefined}
          sequence={index}
        />
      )
      break
    case 'ModuleScatteredMedia':
      renderModule = (
        <DynamicModuleScatteredMedia
          _id={_module._key}
          // theme-dark specified on child element to accomodate background color
          className={`theme-dark module-wrapper ${stegaClean(_module.spacing?.top)} ${stegaClean(_module.spacing?.bottom)}`}
          content={{
            // @ts-ignore: complains of passing undefined as value
            description: _module.description,
            // @ts-ignore: update when image solution available
            images: _module.images,
            // @ts-ignore: update when image solution available
            spacing: _module.spacing,
            title: _module.title
          }}
          labels={undefined}
          sequence={index}
        />
      )
      break
    case 'ModuleShowDetail':
      renderModule = (
        <DynamicModuleShowDetail
          _id={_module._key}
          className='theme-light module-wrapper pt-4 spacing-top-default'
          content={{
            // @ts-ignore: update when solution available
            name: _module.targetShow?.name || 'Unknown',
            // @ts-ignore: update when solution available
            dateTiles: _module.dateTiles,
            // @ts-ignore: update when solution available
            description: _module.targetShow?.description,
            // @ts-ignore: update when solution available
            endDate: _module.targetShow?.endDate,
            // @ts-ignore: update when image solution available
            image: _module.targetShow?.featuredImagePortrait,
            // @ts-ignore: update when solution available
            showId: _module.targetShow?.externalId,
            // @ts-ignore: update when solution available
            startDate: _module.targetShow?.startDate,
            // @ts-ignore: update when solution available
            ticketsAvailable: _module.targetShow?.ticketsAvailableFrom
          }}
          labels={undefined}
          sequence={index}
        />
      )
      break
    case 'ModuleSlideshow':
      renderModule = (
        <DynamicModuleSlideshow
          _id={_module._key}
          className={`theme-light module-wrapper ${stegaClean(_module.spacing?.top)} ${stegaClean(_module.spacing?.bottom)}`}
          content={{
            cta: _module.callToAction,
            // @ts-ignore: update when image solution available
            images: _module.images,
            title: _module.title
          }}
          labels={undefined}
          sequence={index}
        />
      )
      break
    case 'ModuleTheatreDetail':
      renderModule = (
        <DynamicModuleTheatreDetail
          _id={_module._key}
          className='theme-light module-wrapper spacing-top-default'
          content={{
            title: _module.title || undefined,
            // @ts-ignore: update when solution available
            subtitle: _module.subtitle || undefined,
            // @ts-ignore: update when image solution available
            image: _module.image,
            // @ts-ignore: update when solution available
            leftColumn: _module.leftColumn,
            // @ts-ignore: update when solution available
            rightColumn: _module.rightColumn
          }}
          labels={undefined}
          sequence={index}
        />
      )
      break
    case 'ModuleXUp':
      renderModule = (
        <DynamicModuleXUp
          _id={_module._key}
          className={`theme-light module-wrapper ${stegaClean(_module.spacing?.top)} ${stegaClean(_module.spacing?.bottom)}`}
          content={{
            cta: _module.callToAction,
            // @ts-ignore: update when solution available
            cards: _module.cardsToDisplay,
            cardType: _module.cardType,
            title: _module.title
          }}
          labels={undefined}
          sequence={index}
        />
      )
      break
    case 'ModuleXUpDynamic':
      renderModule = (
        <DynamicModuleXUpDynamic
          _id={_module._key}
          className={`theme-light module-wrapper ${stegaClean(_module.spacing?.top)} ${stegaClean(_module.spacing?.bottom)}`}
          content={{
            title: _module.title || undefined,
            cardType: 'product'
          }}
          labels={undefined}
          sequence={index}
        />
      )
      break
    default:
      break
  }
  return (
    <div className='module-container' data-sanity={dataSanity}>
      {renderModule}
      <ScrollObserver />
    </div>
  )
}

export default SanityModule

const DynamicModuleCalendar = dynamic(
  async () => {
    const component = await import('@a24/modules/mCalendar')
    return { default: component.default }
  },
  { ssr: true }
)

const DynamicModuleDivider = dynamic(
  async () => {
    const component = await import('@a24/modules/mDivider')
    return { default: component.default }
  },
  { ssr: true }
)

const DynamicModuleFiftyFifty = dynamic(
  async () => {
    const component = await import('@a24/modules/mFiftyFifty')
    return { default: component.default }
  },
  { ssr: true }
)

const DynamicModuleFreetext = dynamic(
  async () => {
    const component = await import('@a24/modules/mFreetext')
    return { default: component.default }
  },
  { ssr: true }
)

const DynamicModuleGallery = dynamic(
  async () => {
    const component = await import('@a24/modules/mGallery')
    return { default: component.default }
  },
  { ssr: true }
)

const DynamicModuleGrid = dynamic(
  async () => {
    const component = await import('@a24/modules/mGrid')
    return { default: component.default }
  },
  { ssr: true }
)

const DynamicModuleHero = dynamic(
  async () => {
    const component = await import('@a24/modules/mHero')
    return { default: component.default }
  },
  { ssr: true }
)

const DynamicModuleListOf = dynamic(
  async () => {
    const component = await import('@a24/modules/mListOf')
    return { default: component.default }
  },
  { ssr: true }
)

const DynamicModuleProductDetail = dynamic(
  async () => {
    const component = await import('@a24/modules/mProductDetail')
    return { default: component.default }
  },
  { ssr: true }
)

const DynamicModuleScatteredMedia = dynamic(
  async () => {
    const component = await import('@a24/modules/mScatteredMedia')
    return { default: component.default }
  },
  { ssr: true }
)

const DynamicModuleShowDetail = dynamic(
  async () => {
    const component = await import('@a24/modules/mShowDetail')
    return { default: component.default }
  },
  { ssr: true }
)

const DynamicModuleSlideshow = dynamic(
  async () => {
    const component = await import('@a24/modules/mSlideshow')
    return { default: component.default }
  },
  { ssr: true }
)

const DynamicModuleTheatreDetail = dynamic(
  async () => {
    const component = await import('@a24/modules/mTheatreDetail')
    return { default: component.default }
  },
  { ssr: true }
)

const DynamicModuleXUp = dynamic(
  async () => {
    const component = await import('@a24/modules/mXUp')
    return { default: component.default }
  },
  { ssr: true }
)

const DynamicModuleXUpDynamic = dynamic(
  async () => {
    const component = await import('@a24/modules/mXUpDynamic')
    return { default: component.default }
  },
  { ssr: true }
)
